/* RESPONSIBLE TEAM: team-inbox */
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { request } from 'embercom/lib/inbox/requests';
import type AppService from 'embercom/services/app-service';
import type IntlService from 'embercom/services/intl';
import type Session from 'embercom/services/session';

type Context = 'view' | 'search';

interface SerializedListOption {
  id: string | number;
  label: string;
  archived?: boolean;
  available_in_views?: boolean;
  available_in_search?: boolean;
}

interface SerializedAttribute {
  id: string | number;
  name: string;
  identifier: string;
  type: string;
  icon?: string;
  description?: string;
  archived?: boolean;
  category?: string;
  filterable_in_views?: boolean;
  filterable_in_search?: boolean;
  is_system_defined?: boolean;
  editable?: boolean;
  is_classification_attribute?: boolean;
  comparison_operators?: string[] | null;
  list_options?: SerializedListOption[];
  admin_type?: string;
  direction?: string;
  suffix?: string;
}

interface ListOptionMetadata {
  id: string | number;
  available_in_views?: boolean;
  available_in_search?: boolean;
}

interface NormalizedListOption {
  identifier: string | number;
  value: string;
  archived: boolean;
  // Allow dynamic property access for Ember Data compatibility
  [key: string]: unknown;
}

interface NormalizedAttribute {
  id: string | number;
  name: string;
  identifier: string;
  type: string;
  icon?: string;
  description?: string;
  archived: boolean;
  category?: string;
  hideFromInboxViews: boolean;
  hideFromInboxSearch: boolean;
  systemDefined: boolean;
  editable: boolean;
  cda_id: number | null;
  isClassificationAttribute: boolean;
  comparisonOperators: string[] | null;
  _listOptionsMetadata: ListOptionMetadata[] | null;
  _allOptions: NormalizedListOption[];
  adminType?: string;
  direction?: string;
  suffix?: string;
  crm_readable: boolean;
  // Allow dynamic property access for Ember Data compatibility
  [key: string]: unknown;
}

interface TicketTypeGroup {
  ticket_type_id: number;
  ticket_type_name: string;
  ticket_type_category?: string;
  ticket_type_emoji?: string;
  attributes: NormalizedAttribute[];
}

interface FilterableAttributesResponse {
  filterable_attributes: {
    system_attributes?: SerializedAttribute[];
    custom_attributes?: SerializedAttribute[];
    ticket_type_attributes?: SerializedTicketTypeGroup[];
  };
}

interface SerializedTicketTypeGroup {
  ticket_type_id: number;
  ticket_type_name: string;
  ticket_type_category?: string;
  ticket_type_emoji?: string;
  attributes?: SerializedAttribute[];
}

interface EmberDataCompatibleOption {
  identifier: string | number;
  value: string;
  archived: boolean;
  get(key: string): unknown;
}

type EmberDataCompatibleOptionsArray = Array<EmberDataCompatibleOption> & {
  toArray(): EmberDataCompatibleOptionsArray;
  filter(callback: (option: EmberDataCompatibleOption) => boolean): EmberDataCompatibleOptionsArray;
};

interface EmberDataCompatibleWrapper {
  id: string | number;
  name: string;
  identifier: string;
  type: string;
  icon?: string;
  description?: string;
  category?: string;
  archived: boolean;
  hideFromInboxViews: boolean;
  hideFromInboxSearch: boolean;
  systemDefined: boolean;
  editable: boolean;
  isClassificationAttribute: boolean;
  comparisonOperators: string[] | null;
  get(key: string): unknown;
  get options(): EmberDataCompatibleOptionsArray;
  // Allow dynamic property access for .get() method
  [key: string]: unknown;
}

export interface AttributeWithListOptions extends NormalizedAttribute {
  listOptions?: Array<{
    id: string | number;
    label: string;
    archived: boolean;
  }>;
}

export interface TicketDescriptorData {
  id: number;
  name: string;
  identifier?: string;
  type: string;
  dataType?: string;
  icon?: string;
  archived: boolean;
  ticketTypeId: number;
  listOptions?: Array<{
    id: string | number;
    label: string;
    archived: boolean;
  }> | null;
}

export interface TicketTypeData {
  id: number;
  name: string;
  category?: string;
  archived: boolean;
  descriptors?: TicketDescriptorData[] | null;
}

interface AttributeGroup {
  heading?: string;
  category?: string;
  attributes: EmberDataCompatibleWrapper[];
}

interface FilterItem {
  heading?: string;
  items: Array<{
    attribute: AttributeWithListOptions;
    groupId: number;
  }>;
  id: number;
}

/**
 * FilterableAttributesService
 *
 * Fetches unified filterable attributes from the backend endpoint and stores them
 * as plain JavaScript objects. Provides compatibility wrappers for consumers that
 * expect Ember Data records (e.g., predicates system).
 *
 * Backend Architecture (as of PR #446042 & #446157):
 * - Returns FilterableAttribute objects via FilterableAttributesSerializer
 * - Types are normalized: 'decimal' → 'float', 'list' → 'list'
 * - All SDAs are filterable by default
 * - Monitoring via DataDog instrumentation
 *
 * Frontend Storage:
 * - Stores plain JavaScript objects internally (no Ember Data)
 * - Provides compatibility wrappers for views that need Ember Data-like interface
 * - Returns plain objects directly for search (no conversion needed)
 *
 * @class FilterableAttributesService
 * @extends Service
 */
// Default icon for attributes when backend doesn't provide one
const DEFAULT_ATTRIBUTE_ICON = 'transfer';

// Attribute types that are not supported by predicates system (views and search)
// These types will throw errors in predicate.js getGenericType() if not filtered out:
// - 'files': File upload attributes cannot be used as filter criteria
// - 'domain_object_relationship': Custom object relationships not supported
// - 'user_relationship': User relationship attributes not supported
// - 'relationship': Generic relationship type not supported
const UNSUPPORTED_PREDICATE_TYPES = [
  'files',
  'domain_object_relationship',
  'user_relationship',
  'relationship',
];

export default class FilterableAttributesService extends Service {
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare session: Session;

  @tracked _systemAttributes: NormalizedAttribute[] = [];
  @tracked _customAttributes: NormalizedAttribute[] = [];
  @tracked _ticketTypeAttributes: TicketTypeGroup[] = [];
  @tracked _context: Context | null = null;
  @tracked _loadedContexts: Set<Context> = new Set();
  @tracked _isLoading = false;
  @tracked _loadPromise: Promise<void> | null = null;

  /**
   * Check if the feature flag is enabled
   */
  get isFeatureEnabled(): boolean {
    return (
      this.appService.app?.canUseFeature('team-inbox-unified-filterable-attributes') ??
      this.session.workspace.canUseFeature('team-inbox-unified-filterable-attributes')
    );
  }

  /**
   * Check if attributes are loaded for a context
   */
  isLoadedForContext(context: Context): boolean {
    return this._loadedContexts.has(context);
  }

  /**
   * Load filterable attributes for a specific context
   * Memoized - only loads once per context
   * @param context - 'view' or 'search'
   * @returns Promise that resolves when attributes are loaded
   */
  async loadForContext(context: Context): Promise<void> {
    if (this.isLoadedForContext(context)) {
      return this._loadPromise || Promise.resolve();
    }

    if (this._isLoading && this._loadPromise) {
      return this._loadPromise;
    }

    this._isLoading = true;
    this._context = context;

    this._loadPromise = (async () => {
      try {
        await this._fetchAttributes(context);
        this._loadedContexts.add(context);
        this._isLoading = false;
      } catch (error) {
        this._isLoading = false;
        this._loadedContexts.delete(context);
        throw error;
      }
    })();

    return this._loadPromise;
  }

  get appId(): string | undefined {
    if (this.appService.app) {
      return this.appService.app.id;
    } else if (this.session?.isWorkspaceLoaded) {
      return this.session.workspace.id;
    }
    return undefined;
  }

  /**
   * Fetch attributes from backend and store as plain JavaScript objects
   * Merges with existing attributes to preserve attributes from other contexts
   * @private
   */
  async _fetchAttributes(context: Context): Promise<void> {
    try {
      let response = await request(
        `/ember/inbox/filterable_attributes?app_id=${this.appId}&context=${context}`,
      );

      let json = (await response.json()) as FilterableAttributesResponse;

      // Normalize the fetched attributes
      let newSystemAttributes = this._normalizeAttributes(
        json.filterable_attributes.system_attributes || [],
      );
      let newCustomAttributes = this._normalizeAttributes(
        json.filterable_attributes.custom_attributes || [],
      );
      let newTicketTypeAttributes = this._normalizeTicketTypeGroups(
        json.filterable_attributes.ticket_type_attributes || [],
      );

      // Merge with existing attributes to preserve attributes from other contexts
      // Backend already provides correct visibility flags for each attribute
      this._systemAttributes = this._mergeAttributes(this._systemAttributes, newSystemAttributes);
      this._customAttributes = this._mergeAttributes(this._customAttributes, newCustomAttributes);
      this._ticketTypeAttributes = this._mergeTicketTypeGroups(
        this._ticketTypeAttributes,
        newTicketTypeAttributes,
      );
    } catch (error) {
      console.error('[FilterableAttributesService] Failed to fetch:', error);
      throw error;
    }
  }

  /**
   * Merge attributes by deduplicating on identifier
   * Backend provides correct visibility flags, so we just need unique instances
   * @param existingAttrs - Currently cached attributes
   * @param newAttrs - Newly fetched attributes
   * @returns Deduplicated array of attributes
   * @private
   */
  _mergeAttributes(
    existingAttrs: NormalizedAttribute[],
    newAttrs: NormalizedAttribute[],
  ): NormalizedAttribute[] {
    let attrsMap = new Map<string, NormalizedAttribute>();

    // Add existing attributes first
    for (let attr of existingAttrs) {
      attrsMap.set(attr.identifier, attr);
    }

    // Add/overwrite with new attributes (they have up-to-date data)
    for (let attr of newAttrs) {
      attrsMap.set(attr.identifier, attr);
    }

    return Array.from(attrsMap.values());
  }

  /**
   * Merge ticket type groups by deduplicating on ticket_type_id
   * @param existingGroups - Currently cached ticket type groups
   * @param newGroups - Newly fetched ticket type groups
   * @returns Deduplicated array of ticket type groups
   * @private
   */
  _mergeTicketTypeGroups(
    existingGroups: TicketTypeGroup[],
    newGroups: TicketTypeGroup[],
  ): TicketTypeGroup[] {
    let groupsMap = new Map<number, TicketTypeGroup>();

    // Add existing groups first
    for (let group of existingGroups) {
      groupsMap.set(group.ticket_type_id, group);
    }

    // Merge new groups - for groups that exist in both, merge their attributes
    for (let newGroup of newGroups) {
      let existingGroup = groupsMap.get(newGroup.ticket_type_id);
      if (existingGroup) {
        groupsMap.set(newGroup.ticket_type_id, {
          ...newGroup,
          attributes: this._mergeAttributes(existingGroup.attributes, newGroup.attributes),
        });
      } else {
        groupsMap.set(newGroup.ticket_type_id, newGroup);
      }
    }

    return Array.from(groupsMap.values());
  }

  /**
   * Normalize serialized attributes to plain JavaScript objects
   * @param attributes - Array of serialized attribute data from backend
   * @returns Array of plain JavaScript attribute objects
   * @private
   */
  _normalizeAttributes(attributes: SerializedAttribute[]): NormalizedAttribute[] {
    return attributes.map((attr) => this._normalizeAttribute(attr));
  }

  /**
   * Normalize a single attribute to a plain JavaScript object
   * Stores ALL list options with their availability flags for later filtering
   * @param attr - Serialized attribute data from backend
   * @returns Plain JavaScript attribute object
   * @private
   */
  _normalizeAttribute(attr: SerializedAttribute): NormalizedAttribute {
    // Use backend icon if available, otherwise use default icon ('transfer')
    // Special handling for classification attributes which always use 'fin' icon
    let icon = attr.icon || DEFAULT_ATTRIBUTE_ICON;
    if (attr.is_classification_attribute) {
      icon = 'fin';
    }

    return {
      id: attr.id,
      name: attr.name,
      identifier: attr.identifier,
      type: attr.type, // Store normalized type directly
      icon,
      description: attr.description,
      archived: attr.archived || false,
      category: attr.category,
      // Default to showing attributes unless explicitly marked as not filterable
      hideFromInboxViews: attr.filterable_in_views === false,
      hideFromInboxSearch: attr.filterable_in_search === false,
      systemDefined: attr.is_system_defined || false,
      editable: attr.editable !== undefined ? attr.editable : true,
      cda_id: typeof attr.id === 'number' ? attr.id : null,
      isClassificationAttribute: attr.is_classification_attribute || false,
      // Store comparison operators from backend
      comparisonOperators: attr.comparison_operators || null,
      // Store raw list options data with availability flags for context-based filtering
      _listOptionsMetadata: (attr.list_options || []).map((option) => ({
        id: option.id,
        available_in_views: option.available_in_views,
        available_in_search: option.available_in_search,
      })),
      // Store all list options as plain objects (we'll filter by context when retrieving)
      _allOptions: (attr.list_options || []).map((option) => ({
        identifier: option.id,
        value: option.label,
        archived: option.archived || false,
      })),
      // Additional metadata
      adminType: attr.admin_type,
      direction: attr.direction,
      suffix: attr.suffix,
      crm_readable: true,
    };
  }

  /**
   * Normalize ticket type attribute groups to plain JavaScript objects
   * @param groups - Array of ticket type groups from backend
   * @returns Array of plain JavaScript ticket type group objects
   * @private
   */
  _normalizeTicketTypeGroups(groups: SerializedTicketTypeGroup[]): TicketTypeGroup[] {
    return groups.map((group) => ({
      ticket_type_id: group.ticket_type_id,
      ticket_type_name: group.ticket_type_name,
      ticket_type_category: group.ticket_type_category,
      ticket_type_emoji: group.ticket_type_emoji,
      attributes: this._normalizeAttributes(group.attributes || []),
    }));
  }

  /**
   * Get all system attributes (reactive)
   */
  get systemAttributes(): NormalizedAttribute[] {
    return this._systemAttributes;
  }

  /**
   * Get all custom attributes (reactive)
   */
  get customAttributes(): NormalizedAttribute[] {
    return this._customAttributes;
  }

  /**
   * Get all ticket type attribute groups (reactive)
   * Returns TicketTypeGroup with NormalizedAttribute[], use ticketTypeAttributesAsData for TicketDescriptorData[]
   */
  get ticketTypeAttributes(): TicketTypeGroup[] {
    return this._ticketTypeAttributes;
  }

  /**
   * Get ticket type attributes formatted as TicketTypeData (for search component compatibility)
   * Converts NormalizedAttribute to TicketDescriptorData
   */
  get ticketTypeAttributesAsData(): TicketTypeData[] {
    return this._ticketTypeAttributes.map((group) => ({
      id: group.ticket_type_id,
      name: group.ticket_type_name,
      category: group.ticket_type_category,
      archived: false,
      descriptors: group.attributes.map((attr) => ({
        id: attr.id as number,
        name: attr.name,
        identifier: attr.identifier,
        type: attr.type,
        dataType: attr.type,
        icon: attr.icon,
        archived: attr.archived,
        ticketTypeId: group.ticket_type_id,
        listOptions:
          attr._allOptions.length > 0
            ? attr._allOptions.map((option) => ({
                id: option.identifier,
                label: option.value,
                archived: option.archived,
              }))
            : null,
      })),
    }));
  }

  /**
   * Get all attributes filtered by context (for search descriptor building)
   * Returns attributes with filtered list options based on context
   * For view: returns Ember Data-compatible wrappers
   * For search: returns plain objects
   * @param context - 'view' or 'search'
   * @returns Array of attribute objects with filtered options
   */
  getAttributesForContext(
    context: Context,
  ): (EmberDataCompatibleWrapper | AttributeWithListOptions)[] {
    let allAttributes = [
      ...this._systemAttributes,
      ...this._customAttributes,
      ...this._ticketTypeAttributes.flatMap((group) => group.attributes || []),
    ];

    // Filter attributes by context
    let filteredAttributes = this._filterForContext(allAttributes, context);

    // For view, return Ember Data-compatible wrappers
    // For search, return plain objects with filtered options
    if (context === 'view') {
      return this._createEmberDataCompatibleWrappers(filteredAttributes, context);
    } else {
      return this._applyContextFilteringToPlainObjects(filteredAttributes, context);
    }
  }

  /**
   * Get attribute groups formatted for inbox views (predicates-editor)
   * Returns synchronously - must call loadForContext() first
   * Filters list options by context when returning attributes
   * Returns Ember Data-compatible wrappers for predicate system compatibility
   * @returns Array of attribute groups
   */
  get attributeGroupListForViews(): AttributeGroup[] {
    let groups: AttributeGroup[] = [];

    // System attributes group (no heading)
    let filteredSystemAttrs = this._filterForContext(this._systemAttributes, 'view');
    let systemAttrs = this._createEmberDataCompatibleWrappers(filteredSystemAttrs, 'view');
    if (systemAttrs.length > 0) {
      groups.push({ attributes: systemAttrs });
    }

    // Custom conversation attributes group
    let filteredConversationAttrs = this._filterForContext(
      this._customAttributes.filter((attr) => attr.category === 'conversation'),
      'view',
    );
    let conversationAttrs = this._createEmberDataCompatibleWrappers(
      filteredConversationAttrs,
      'view',
    );
    if (conversationAttrs.length > 0) {
      groups.push({
        heading: this.intl.t('inbox.inbox-views.predicates.conversation-attribute-heading'),
        attributes: conversationAttrs,
      });
    }

    // Ticket type groups
    this._ticketTypeAttributes.forEach((group) => {
      let filteredTicketAttrs = this._filterForContext(group.attributes, 'view');
      let ticketAttrs = this._createEmberDataCompatibleWrappers(filteredTicketAttrs, 'view');
      if (ticketAttrs.length > 0) {
        groups.push({
          heading: group.ticket_type_name,
          category: group.ticket_type_category,
          attributes: ticketAttrs,
        });
      }
    });

    return groups;
  }

  /**
   * Get filter items formatted for inbox search
   * Returns plain objects with filtered list options
   * @returns Array of filter groups
   */
  get filterItemsForSearch(): FilterItem[] {
    let items: FilterItem[] = [];

    // System attributes (no category)
    let systemAttrs = this._filterForContext(
      this._systemAttributes.filter((attr) => !attr.category),
      'search',
    );
    let systemPlainAttrs = this._applyContextFilteringToPlainObjects(systemAttrs, 'search');
    if (systemPlainAttrs.length > 0) {
      items.push({
        heading: undefined,
        items: systemPlainAttrs.map((attr) => ({ attribute: attr, groupId: -2 })),
        id: -2,
      });
    }

    // Conversation data attributes (both system and custom with category="conversation")
    let systemConversationAttrs = this._filterForContext(
      this._systemAttributes.filter((attr) => attr.category === 'conversation'),
      'search',
    );
    let customConversationAttrs = this._filterForContext(
      this._customAttributes.filter((attr) => attr.category === 'conversation'),
      'search',
    );
    let allConversationAttrs = [...systemConversationAttrs, ...customConversationAttrs];
    let conversationPlainAttrs = this._applyContextFilteringToPlainObjects(
      allConversationAttrs,
      'search',
    );

    if (conversationPlainAttrs.length > 0) {
      items.push({
        heading: this.intl.t('inbox.search.filters.more-filters.conversation-data-attributes'),
        items: conversationPlainAttrs.map((attr) => ({ attribute: attr, groupId: -1 })),
        id: -1,
      });
    }

    // Scorecard attributes (system only with category="qa")
    // Only show if rd-services-fin-qa feature flag is enabled
    if (this.appService.app?.canUseFeature('rd-services-fin-qa')) {
      let systemQaAttrs = this._filterForContext(
        this._systemAttributes.filter((attr) => attr.category === 'qa'),
        'search',
      );
      let qaPlainAttrs = this._applyContextFilteringToPlainObjects(systemQaAttrs, 'search');

      let monitorAttribute: AttributeWithListOptions = {
        id: 'monitor',
        identifier: 'conversation.custom_attribute.qa_monitor_ids',
        name: this.intl.t('inbox.search.filters.more-filters.monitor'),
        type: 'monitor',
        icon: undefined,
        archived: false,
        category: 'qa',
        hideFromInboxViews: true,
        hideFromInboxSearch: false,
        systemDefined: true,
        editable: false,
        cda_id: null,
        isClassificationAttribute: false,
        comparisonOperators: null,
        _listOptionsMetadata: [],
        _allOptions: [],
        listOptions: [],
        crm_readable: false,
      };

      items.push({
        heading: this.intl.t('inbox.search.filters.more-filters.scorecard-attributes'),
        items: [
          ...qaPlainAttrs.map((attr) => ({ attribute: attr, groupId: -3 })),
          { attribute: monitorAttribute, groupId: -3 },
        ],
        id: -3,
      });
    }

    // Ticket type groups
    this._ticketTypeAttributes.forEach((group) => {
      let ticketAttrs = this._filterForContext(group.attributes, 'search');
      let ticketPlainAttrs = this._applyContextFilteringToPlainObjects(ticketAttrs, 'search');
      if (ticketPlainAttrs.length > 0) {
        items.push({
          heading: group.ticket_type_name,
          items: ticketPlainAttrs.map((attr) => ({
            attribute: attr,
            groupId: group.ticket_type_id,
          })),
          id: group.ticket_type_id,
        });
      }
    });

    return items;
  }

  /**
   * Apply context-based filtering to plain attribute objects
   * Filters list options by context and returns plain objects with listOptions
   * @param attributes - Array of plain attribute objects
   * @param context - 'view' or 'search'
   * @returns Array of plain attribute objects with filtered listOptions
   * @private
   */
  _applyContextFilteringToPlainObjects(
    attributes: NormalizedAttribute[],
    context: Context,
  ): AttributeWithListOptions[] {
    return attributes.map((attr) => {
      // Get filtered options for this context
      let filteredOptions = this._getFilteredOptionsForPlainObject(attr, context);

      // Create a copy with filtered listOptions
      let filteredAttr: AttributeWithListOptions = {
        ...attr,
      };

      // Add listOptions if this is a list-type attribute
      if (filteredOptions && filteredOptions.length > 0) {
        filteredAttr.listOptions = filteredOptions.map((option) => ({
          id: option.identifier,
          label: option.value,
          archived: option.archived,
        }));
      } else if (attr.type === 'list' || attr.type === 'conversation_attribute_list') {
        // For list types with no options, set empty array
        filteredAttr.listOptions = [];
      }

      return filteredAttr;
    });
  }

  /**
   * Create Ember Data-compatible wrappers for plain attribute objects
   * Used for view context (predicates-editor) where the predicate system expects Ember Data-like interface
   * @param attributes - Array of plain attribute objects
   * @param context - 'view' or 'search'
   * @returns Array of wrapped attributes with Ember Data-like interface
   * @private
   */
  _createEmberDataCompatibleWrappers(
    attributes: NormalizedAttribute[],
    context: Context,
  ): EmberDataCompatibleWrapper[] {
    return attributes.map((attr) => {
      // Get filtered options for this context
      let filteredOptions = this._getFilteredOptionsForPlainObject(attr, context);

      // Create a wrapper object that provides Ember Data-like interface
      let wrapper: EmberDataCompatibleWrapper = {
        // Direct property access (for compatibility with code that uses attr.identifier, etc.)
        id: attr.id,
        name: attr.name,
        identifier: attr.identifier,
        // Convert 'list' type to 'conversation_attribute_list' for predicate compatibility
        // Backend returns normalized 'list' type, but predicates expect 'conversation_attribute_list'
        type: attr.type === 'list' ? 'conversation_attribute_list' : attr.type,
        icon: attr.icon,
        description: attr.description,
        category: attr.category,
        archived: attr.archived,
        hideFromInboxViews: attr.hideFromInboxViews,
        hideFromInboxSearch: attr.hideFromInboxSearch,
        systemDefined: attr.systemDefined,
        editable: attr.editable,
        isClassificationAttribute: attr.isClassificationAttribute,
        comparisonOperators: attr.comparisonOperators,

        // Ember Data .get() method compatibility
        // Used by predicate.js to access comparisonOperators and other properties
        get(key: string): unknown {
          if (key === 'options') {
            return filteredOptions;
          }
          if (key === 'type') {
            // Return the converted type for consistency
            return wrapper.type;
          }
          // Access the wrapper's own properties first, then fall back to underlying attr
          // This ensures .get('comparisonOperators') returns the correct value
          return (wrapper as Record<string, unknown>)[key] ?? attr[key];
        },

        // Options relationship (Ember Data-compatible)
        // Returns an array-like object with .toArray() method and .get() on each option
        get options(): EmberDataCompatibleOptionsArray {
          let optionsArray = filteredOptions.map((option) => ({
            identifier: option.identifier,
            value: option.value,
            archived: option.archived,
            // Ember Data .get() compatibility for options
            get(key: string): unknown {
              return option[key];
            },
          })) as EmberDataCompatibleOptionsArray;

          // Add .toArray() method for Ember Data compatibility
          optionsArray.toArray = function (): EmberDataCompatibleOptionsArray {
            return this;
          };

          // Add .filter() method that returns array with .toArray()
          // We need to override the filter method to ensure it returns an array with toArray() method
          let originalFilter = optionsArray.filter.bind(optionsArray);
          Object.defineProperty(optionsArray, 'filter', {
            value(
              callback: (option: EmberDataCompatibleOption) => boolean,
            ): EmberDataCompatibleOptionsArray {
              let filtered = originalFilter(callback) as EmberDataCompatibleOptionsArray;
              filtered.toArray = function (): EmberDataCompatibleOptionsArray {
                return this;
              };
              return filtered;
            },
            writable: true,
            enumerable: false,
            configurable: true,
          });

          return optionsArray;
        },
      };

      return wrapper;
    });
  }

  /**
   * Get options for a plain attribute object filtered by context
   * @param attr - Plain attribute object
   * @param context - 'view' or 'search'
   * @returns Filtered list of plain option objects
   * @private
   */
  _getFilteredOptionsForPlainObject(
    attr: NormalizedAttribute,
    context: Context,
  ): NormalizedListOption[] {
    let listOptionsMetadata = attr._listOptionsMetadata;
    if (!listOptionsMetadata || listOptionsMetadata.length === 0) {
      // No metadata, return all options
      return attr._allOptions || [];
    }

    // Filter options based on context availability flags
    let availableOptionIds = listOptionsMetadata
      .filter((option) => {
        if (context === 'view') {
          return option.available_in_views !== false; // Include if true or undefined
        } else if (context === 'search') {
          return option.available_in_search !== false; // Include if true or undefined
        }
        return true; // If context is not specified, include all options
      })
      .map((option) => option.id);

    // Filter the options based on available IDs
    return (attr._allOptions || []).filter((option) =>
      availableOptionIds.includes(option.identifier),
    );
  }

  /**
   * Check if an attribute type is supported by the predicates system
   * @param type - The attribute type to check
   * @returns true if the type is supported, false otherwise
   * @private
   */
  _isTypeSupported(type: string): boolean {
    return !UNSUPPORTED_PREDICATE_TYPES.includes(type);
  }

  /**
   * Filter attributes based on context and supported types
   * Backend already filters by context, but we double-check for safety
   * Also filters out attribute types that are not supported by predicates system
   * @param attributes - Array of plain attribute objects
   * @param context - 'view' or 'search'
   * @returns Filtered array of attribute objects
   * @private
   */
  _filterForContext(attributes: NormalizedAttribute[], context: Context): NormalizedAttribute[] {
    // First filter by supported types (files, relationship types are not supported)
    let supportedAttributes = attributes.filter((attr) => this._isTypeSupported(attr.type));

    // Then filter by context-specific visibility
    if (context === 'view') {
      return supportedAttributes.filter((attr) => !attr.hideFromInboxViews);
    } else {
      return supportedAttributes.filter((attr) => !attr.hideFromInboxSearch);
    }
  }

  /**
   * Reset service state
   */
  reset(): void {
    this._systemAttributes = [];
    this._customAttributes = [];
    this._ticketTypeAttributes = [];
    this._context = null;
    this._loadedContexts.clear();
    this._isLoading = false;
    this._loadPromise = null;
  }
}

declare module '@ember/service' {
  interface Registry {
    'filterable-attributes': FilterableAttributesService;
    filterableAttributesService: FilterableAttributesService;
  }
}
